import React, { useEffect, useMemo, useState } from 'react';
import { ModalHandle, StatusCard, toArray } from 'Mri.Web.Common';
import { Button, Col, Collapse, ListGroup, ListGroupItem, Row } from 'reactstrap';
import { observer } from 'mobx-react';
import classNames from 'classnames/bind';
import { ActiveDisplay } from '../../ActiveDisplay';
import { useStore } from '../../../hooks/use-store';
import { ServiceKit } from '../../../ServiceKits/ServiceKitStore';
import { EditDisplayServiceKitsModal } from '../../../ServiceKits/EditDisplayServiceKitsModal';
import style from './DisplayServiceKitsCard.module.scss';
import { BsChevronUp } from '@react-icons/all-files/bs/BsChevronUp';

const classes = classNames.bind(style);
interface props {
    display: ActiveDisplay;
}

export const DisplayServiceKitsCard = observer((props: props) => {
    const { display } = props;
    const [loading, setLoading] = useState(false);
    const [initialSetupComplete, setInitialSetupComplete] = useState(false);
    const [openServiceKitAccordion, setOpenServiceKitAccordion] = useState([] as string[]);
    const [serviceKits, setServiceKits] = useState([] as ServiceKit[]);
    const { displaysStore } = useStore();
    const editServiceKitsModalHandle = useMemo(() => new ModalHandle(), []);

    useEffect(() => {
        loadServiceKits();
        displaysStore.setActiveDisplay(display as ActiveDisplay);
    }, [display.serialNumber]);

    useEffect(() => {
        setServiceKits(displaysStore.activeDisplayServiceKitsList as ServiceKit[]);
    }, [displaysStore.activeDisplayServiceKitsList]);
    const loadServiceKits = () => {
        setLoading(true);
        displaysStore
            .getAssociatedServiceKits(display.serialNumber)
            .then(data => {
                setServiceKits(data as ServiceKit[]);
            })
            .catch(error => console.error(error))
            .finally(() => {
                setLoading(false);
                setInitialSetupComplete(true);
            });
    };

    const getServiceKitsFromStore = () => {
        setServiceKits(displaysStore.displayServiceKits.get(display.serialNumber) as ServiceKit[]);
    };

    const toggleServiceKitAccordion = (targetId: string) => {
        if (openServiceKitAccordion.includes(targetId)) {
            setOpenServiceKitAccordion(openServiceKitAccordion.filter(id => id !== targetId));
        } else {
            setOpenServiceKitAccordion([...openServiceKitAccordion, targetId]);
        }
    };

    const Title = () => {
        return (
            <div className={'d-flex justify-content-between'}>
                <span>Service Kits</span>
                <Button
                    color={'link'}
                    className={classes('edit-button', 'ml-auto')}
                    onClick={() => editServiceKitsModalHandle.open()}
                >
                    Edit Service Kits
                </Button>
            </div>
        );
    };

    const ServiceKitItem = ({ serviceKit, index }: { serviceKit: ServiceKit; index: number }) => {
        const id = `display-${display.serialNumber}-service-kit-${index}`;
        return (
            <div className={classes('service-kit', { open: openServiceKitAccordion.includes(id) })}>
                <div className={classes('service-kit-name')} onClick={() => toggleServiceKitAccordion(id)}>
                    <span>{serviceKit.name}</span>
                    <BsChevronUp />
                </div>
                <Collapse
                    className={classes('service-kit-body')}
                    key={id}
                    isOpen={openServiceKitAccordion.includes(id)}
                >
                    <Row>
                        <Col>
                            <div className={classes('description')}>{serviceKit.description}</div>
                        </Col>
                        <Col>
                            <ListGroup flush className={classes('assemblies')}>
                                {!serviceKit.assemblies && (
                                    <ListGroupItem>
                                        No Parts or Assemblies are associated with this Service Kit
                                    </ListGroupItem>
                                )}
                                {serviceKit.assemblies &&
                                    serviceKit.assemblies.map((assembly, serviceKitIndex) => {
                                        return (
                                            <ListGroupItem
                                                key={`display-${display.serialNumber}-service-kit-${serviceKitIndex}-assembly-${serviceKitIndex}`}
                                            >
                                                <div className={classes('name')}>{assembly.description}</div>
                                                <div className="description">{assembly.partNumber}</div>
                                            </ListGroupItem>
                                        );
                                    })}
                            </ListGroup>
                        </Col>
                    </Row>
                </Collapse>
            </div>
        );
    };

    return (
        <>
            <EditDisplayServiceKitsModal
                modalHandle={editServiceKitsModalHandle}
                display={display}
                serviceKits={serviceKits}
                onClose={() => loadServiceKits()}
            />
            <StatusCard title={Title()} className={'card'} loading={loading}>
                {!loading && serviceKits.length === 0 && <div key={'no-kits'}>No Service Kits</div>}
                {!loading &&
                    serviceKits.length > 0 &&
                    serviceKits.map((serviceKit, index) => {
                        return (
                            <ServiceKitItem
                                serviceKit={serviceKit}
                                index={index}
                                key={`display-${display.serialNumber}-service-kit-${index}`}
                            />
                        );
                    })}
            </StatusCard>
        </>
    );
});
